/**
 * ТЗ 0093 — реестр раздела «Настройки»: какие разделы есть, в какой группе,
 * куда ведут и КОМУ видны.
 *
 * Одна правда на четыре места: хаб `/settings` рисует карточки отсюда, меню
 * решает, показывать ли «Все настройки», палитра строит команды разделов,
 * `AppRouter` выдаёт маршруты (`ROLE_LISTED_ROUTES`). Роли — ТЕ ЖЕ
 * константы, что у маршрутов, меню и палитры соседних разделов: список,
 * переписанный литералом, разъезжается с оригиналом при первой же правке.
 *
 * Настройки — то, что конфигурируют один раз и что определяет, как работает
 * программа (решение владельца 30.09). Ежедневная работа — курсы, группы,
 * журналы, ручная блокировка, журналы отправок — живёт в своих разделах.
 *
 * Скрытие раздела — UX, а не защита: авторитет у бэкенда. Но гейт нужен и
 * чтобы не слать запрос с чужой роли — отказ рисовал бы ошибку поверх
 * пустого экрана.
 */

import {
  Bot,
  Briefcase,
  CalendarClock,
  Coins,
  Handshake,
  Landmark,
  LayoutTemplate,
  LockKeyhole,
  LucideIcon,
  MailCheck,
  Megaphone,
  MessageSquareText,
  MessagesSquare,
  PlugZap,
  ShieldCheck,
  Signature,
  Sparkles,
  UserRound,
  Video,
} from 'lucide-react';

import { PATHS } from 'router/config';
import { UserRole } from 'types';

import { LEAD_ADMIN_ROLES } from '../Crm/useLeadPermissions';
import { DOFIN_READ_ROLES } from '../Dofinansowanie/useDofinPermissions';
import { MARKETING_ROLES } from '../Marketing/useMarketingPermissions';
import {
  ADMIN_ROLES,
  CAREER_ROLES,
  FINANCE_PLAN_ROLES,
  MANAGEMENT_ROLES,
  MCP_ACCESS_ROLES,
  PAYMENT_ACCESS_ROLES,
  COMMUNICATOR_ROLES,
  visibleFor,
  ZOOM_ROLES,
} from '../Palette/paletteRoles';
import { SMS_ROLES } from '../Sms/useSmsPermissions';
import { INTEGRATION_TABS, IntegrationTab } from './settingsLinks';

/**
 * Группы хаба — в порядке показа. `domains` (ТЗ 0093 этап 2а) — настройки
 * отделов: справочники маркетинга, правила и параметры финансов, тексты
 * карьерного центра.
 */
export const SETTINGS_GROUPS = [
  'automation',
  'messages',
  'domains',
  'integrations',
  'access',
] as const;
export type SettingsGroup = typeof SETTINGS_GROUPS[number];

export type SettingsSectionId =
  | 'robots'
  | 'lmsAccess'
  | 'sms'
  | 'templates'
  | 'dofinMail'
  | 'mailSignatures'
  | 'marketing'
  | 'finance'
  | 'career'
  | 'ai'
  | 'integrations'
  | 'users'
  | 'mcp';

/**
 * ТЗ 0095 — смысловые разделы (домены) настроек. Коды и порядок — те же,
 * что у сервера (`runtimeSettingDomains`, поле `domain` ключей и
 * расписаний); подписи — свои, i18n: `runtimeSettingDomains` открыт только
 * Admin, а чипы видят все роли хаба.
 */
export const SETTINGS_DOMAINS = [
  'marketing',
  'sales',
  'communicator',
  'education',
  'dofin',
  'finance',
  'career',
  'ai',
  'system',
] as const;
export type SettingsDomain = typeof SETTINGS_DOMAINS[number];

/** Значок домена — у чипа хаба и у команды палитры. */
export const SETTINGS_DOMAIN_ICONS: Readonly<
  Record<SettingsDomain, LucideIcon>
> = {
  marketing: Megaphone,
  sales: Handshake,
  communicator: MessagesSquare,
  education: Video,
  dofin: Landmark,
  finance: Coins,
  career: Briefcase,
  ai: Sparkles,
  system: ShieldCheck,
};

export const settingsDomainLabelKey = (domain: SettingsDomain): string =>
  `settingsHub.domains.${domain}`;

export const isSettingsDomain = (value: unknown): value is SettingsDomain =>
  typeof value === 'string' &&
  (SETTINGS_DOMAINS as readonly string[]).includes(value);

/**
 * `section` — раздел живёт в хабе (`/settings/<раздел>`, оболочка «← Все
 * настройки»); `link` — карточка ведёт в самостоятельный раздел программы,
 * у которого свой пункт меню (пользователи, подключённые ИИ).
 */
export type SettingsSectionKind = 'section' | 'link';

/** Куда ведёт карточка: адрес из `PATHS` и, если нужно, вкладка. */
export interface ISettingsTarget {
  pathname: PATHS;
  search?: string;
}

export interface ISettingsSection {
  id: SettingsSectionId;
  group: SettingsGroup;
  /** i18n-ключ названия (хаб, оболочка раздела, палитра). */
  titleKey: string;
  /** i18n-ключ строки «что здесь настраивают». */
  descKey: string;
  icon: LucideIcon;
  to: ISettingsTarget;
  roles: UserRole[];
  kind: SettingsSectionKind;
  /** Слова для поиска в палитре и хабе (ru/pl/en), не ключи i18n. */
  keywords: string[];
  /**
   * ТЗ 0095: смысловые разделы, где показывается карточка. Хотя бы один —
   * тест реестра; одна карточка может жить в нескольких (шаблоны —
   * и письма коммуникатора, и договоры).
   */
  domains: SettingsDomain[];
}

/**
 * Вкладки «Интеграций» и их роли: Kommo правит только Admin (синхронизация,
 * сопоставление, повтор примечаний — `ADMIN_ROLES` бэкенда), Slack — роли
 * коммуникатора (там живут ящики настроек группы), аккаунты Zoom — роли
 * раздела Zoom.
 */
export const INTEGRATION_TAB_ROLES: Readonly<
  Record<IntegrationTab, UserRole[]>
> = {
  kommo: LEAD_ADMIN_ROLES,
  // Перепроверка F33: роли коммуникатора, как и сказано выше, — с HR.
  // Вкладка — гид и «Состояние» (`communicationsHealth`), бэкенд пускает
  // туда весь COMM_ACCESS_ROLES (проверено на стенде под HR).
  slack: COMMUNICATOR_ROLES,
  zoom: ZOOM_ROLES,
};

/** Вкладки «Интеграций», видимые ролям, — в порядке показа. */
export const visibleIntegrationTabs = (
  roles: readonly UserRole[],
): IntegrationTab[] =>
  INTEGRATION_TABS.filter((tab) =>
    visibleFor([...roles], INTEGRATION_TAB_ROLES[tab]),
  );

const unionRoles = (...lists: UserRole[][]): UserRole[] =>
  Array.from(new Set(lists.flat()));

/** Раздел виден, если видна хотя бы одна его вкладка. */
const INTEGRATIONS_ROLES = unionRoles(
  ...INTEGRATION_TABS.map((tab) => INTEGRATION_TAB_ROLES[tab]),
);

/**
 * ТЗ 0093 этап 2б — «Роботы и расписания» и «ИИ» (runtime-настройки).
 * Зеркало бэкенда: `SETTINGS_ROLES` (объединение ролей ключей реестра —
 * сейчас у всех ключей Admin) и `SCHEDULE_ROLES` (Admin). Появится ключ с
 * другой ролью на сервере — список расширяется здесь; какие ключи увидит
 * роль, решает сервер.
 */
export const RUNTIME_SETTINGS_ROLES: UserRole[] = [UserRole.ADMIN];

const titleKey = (id: SettingsSectionId) => `settingsHub.sections.${id}.title`;
const descKey = (id: SettingsSectionId) => `settingsHub.sections.${id}.desc`;

export const SETTINGS_SECTIONS: readonly ISettingsSection[] = [
  {
    // ТЗ 0093 этап 2б: выключатели роботов, каналов, почты, интеграций и
    // пороги (runtime-настройки без пересоздания контейнера), таблица
    // периодических задач и журнал изменений.
    id: 'robots',
    group: 'automation',
    titleKey: titleKey('robots'),
    descKey: descKey('robots'),
    icon: CalendarClock,
    to: { pathname: PATHS.settingsRobots },
    roles: RUNTIME_SETTINGS_ROLES,
    kind: 'section',
    domains: ['system'],
    keywords: [
      'роботы',
      'расписания',
      'периодические задачи',
      'выключатели',
      'каналы',
      'celery',
      'beat',
      'roboty',
      'harmonogramy',
      'zadania okresowe',
      'przełączniki',
      'kanały',
    ],
  },
  {
    // ТЗ 0074: не «приостановка доступа» как таковая (ручная блокировка —
    // в карточке студента и у должников), а настройки робота.
    id: 'lmsAccess',
    group: 'automation',
    titleKey: titleKey('lmsAccess'),
    descKey: descKey('lmsAccess'),
    icon: LockKeyhole,
    to: { pathname: PATHS.settingsLmsAccess },
    roles: PAYMENT_ACCESS_ROLES,
    kind: 'section',
    domains: ['education'],
    keywords: [
      'робот',
      'блокировка',
      'должники',
      'приостановка',
      'lms',
      'robot',
      'blokada',
      'dłużnicy',
      'wstrzymanie',
    ],
  },
  {
    // ТЗ 0093 этап 2б: модели Claude по функциям и выключатели платных
    // ИИ-функций (проверка ДЗ, профиль преподавателя, сводка маркетинга).
    id: 'ai',
    group: 'automation',
    titleKey: titleKey('ai'),
    descKey: descKey('ai'),
    icon: Sparkles,
    to: { pathname: PATHS.settingsAi },
    roles: RUNTIME_SETTINGS_ROLES,
    kind: 'section',
    domains: ['ai'],
    keywords: [
      'ии',
      'ai',
      'модель',
      'модели',
      'claude',
      'anthropic',
      'искусственный интеллект',
      'model',
      'modele',
      'sztuczna inteligencja',
    ],
  },
  {
    id: 'sms',
    group: 'messages',
    titleKey: titleKey('sms'),
    descKey: descKey('sms'),
    icon: MessageSquareText,
    to: { pathname: PATHS.settingsSms },
    roles: SMS_ROLES,
    kind: 'section',
    domains: ['sales'],
    keywords: [
      'sms',
      'смс',
      'smsapi',
      'лесенки',
      'напоминания',
      'адреса ссылок',
      'drabinki',
      'przypomnienia',
      'ustawienia sms',
    ],
  },
  {
    // Содержимое — шаблоны тем SMS и писем (роли SMS) плюс блок шаблонов
    // договоров, который раздел показывает сам: при включённой подписи и
    // праве читать студенческие договоры.
    id: 'templates',
    group: 'messages',
    titleKey: titleKey('templates'),
    descKey: descKey('templates'),
    icon: LayoutTemplate,
    to: { pathname: PATHS.settingsTemplates },
    roles: SMS_ROLES,
    kind: 'section',
    domains: ['communicator', 'sales'],
    keywords: [
      'шаблоны',
      'шаблон',
      'тексты',
      'письма',
      'договоры',
      'templates',
      'szablony',
      'szablon',
      'treści',
    ],
  },
  {
    // ТЗ 0097: свои подписи писем — по умолчанию и по ящикам; их добавляет
    // редактор письма при отправке. Роли — `MANAGEMENT_ROLES` бэкенда
    // (`myMailSignatures` / `saveMailSignature`), их зеркало —
    // `MANAGEMENT_ROLES` (не `MCP_ACCESS_ROLES`: там с ТЗ 0126 и методист).
    id: 'mailSignatures',
    group: 'messages',
    titleKey: titleKey('mailSignatures'),
    descKey: descKey('mailSignatures'),
    icon: Signature,
    to: { pathname: PATHS.settingsMailSignatures },
    roles: MANAGEMENT_ROLES,
    kind: 'section',
    domains: ['communicator'],
    keywords: [
      'подпись',
      'подписи писем',
      'почта',
      'gmail',
      'письма',
      'signature',
      'podpis',
      'podpisy',
      'stopka',
      'poczta',
    ],
  },
  {
    // ТЗ 0085 → 0093: бывшая вкладка «Правила писем» страницы дофина. Роли —
    // читатели домена (`DOFIN_READ_ROLES`); править правила и робота можно
    // тому, кому сервер ответил `canEdit` (FinanceManager — только чтение).
    id: 'dofinMail',
    group: 'messages',
    titleKey: titleKey('dofinMail'),
    descKey: descKey('dofinMail'),
    icon: MailCheck,
    to: { pathname: PATHS.settingsDofinMail },
    roles: DOFIN_READ_ROLES,
    kind: 'section',
    domains: ['dofin'],
    keywords: [
      'дофинансирование',
      'письма',
      'правила писем',
      'рассылка',
      'sendgrid',
      'dofinansowanie',
      'listy',
      'reguły listów',
      'wysyłka',
    ],
  },
  {
    // ТЗ 0088 → 0093: справочники маркетинга (бывшая вкладка «Настройки»
    // страницы). Роли — роли раздела (`MARKETING_ROLES`); править —
    // `MARKETING_EDIT_ROLES`, маршрут сигналов — `MARKETING_ADMIN_ROLES`
    // (решает сам раздел, окончательно — сервер). Источники, план и журнал
    // выгрузки — операционка, они остались на странице маркетинга.
    id: 'marketing',
    group: 'domains',
    titleKey: titleKey('marketing'),
    descKey: descKey('marketing'),
    icon: Megaphone,
    to: { pathname: PATHS.settingsMarketing },
    roles: MARKETING_ROLES,
    kind: 'section',
    domains: ['marketing'],
    keywords: [
      'маркетинг',
      'каналы',
      'правила каналов',
      'офферы',
      'семейства курсов',
      'сигналы',
      'воронки kommo',
      'marketing',
      'kanały',
      'oferty',
      'rodziny kursów',
      'sygnały',
      'lejki',
    ],
  },
  {
    // ТЗ 0023 / 0093: правила разнесения и параметры внутренних переводов
    // cash flow, параметры платёжного календаря. Роли — те же, что у
    // маршрутов cash flow и календаря (`FINANCE_PLAN_ROLES`: Admin,
    // FinanceManager).
    id: 'finance',
    group: 'domains',
    titleKey: titleKey('finance'),
    descKey: descKey('finance'),
    icon: Coins,
    to: { pathname: PATHS.settingsFinance },
    roles: FINANCE_PLAN_ROLES,
    kind: 'section',
    domains: ['finance'],
    keywords: [
      'финансы',
      'cash flow',
      'правила разнесения',
      'внутренние переводы',
      'платёжный календарь',
      'день выплат',
      'finanse',
      'reguły',
      'przelewy wewnętrzne',
      'kalendarz płatności',
    ],
  },
  {
    // ТЗ 0039 / 0093: тексты постов для репоста. Роли — роли карьерного
    // центра (`CAREER_ROLES`); кто правит, решает сервер.
    id: 'career',
    group: 'domains',
    titleKey: titleKey('career'),
    descKey: descKey('career'),
    icon: Briefcase,
    to: { pathname: PATHS.settingsCareer },
    roles: CAREER_ROLES,
    kind: 'section',
    domains: ['career'],
    keywords: [
      'карьерный центр',
      'тексты постов',
      'репост',
      'linkedin',
      'centrum kariery',
      'treści postów',
      'udostępnianie',
    ],
  },
  {
    id: 'integrations',
    group: 'integrations',
    titleKey: titleKey('integrations'),
    descKey: descKey('integrations'),
    icon: PlugZap,
    to: { pathname: PATHS.settingsIntegrations },
    roles: INTEGRATIONS_ROLES,
    kind: 'section',
    domains: ['communicator', 'sales', 'education'],
    keywords: [
      'интеграции',
      'kommo',
      'slack',
      'zoom',
      'аккаунты zoom',
      'integrations',
      'integracje',
      'konta zoom',
    ],
  },
  {
    id: 'users',
    group: 'access',
    titleKey: titleKey('users'),
    descKey: descKey('users'),
    icon: UserRound,
    to: { pathname: PATHS.users },
    roles: ADMIN_ROLES,
    kind: 'link',
    domains: ['system'],
    keywords: ['пользователи', 'роли', 'użytkownicy', 'role', 'users'],
  },
  {
    id: 'mcp',
    group: 'access',
    titleKey: titleKey('mcp'),
    descKey: descKey('mcp'),
    icon: Bot,
    to: { pathname: PATHS.mcpAccess },
    roles: MCP_ACCESS_ROLES,
    kind: 'link',
    domains: ['system'],
    keywords: ['mcp', 'ии', 'ai', 'подключённые ии', 'podłączone ai'],
  },
];

/**
 * Разделы, видимые ролям, — в порядке реестра. Флагов сервера у разделов нет:
 * блок шаблонов договоров «Шаблонов сообщений» прячет себя сам, когда
 * подпись выключена, а раздел остаётся (в нём шаблоны SMS и писем).
 */
export const visibleSettingsSections = (
  roles: readonly UserRole[],
): ISettingsSection[] =>
  SETTINGS_SECTIONS.filter((section) => visibleFor([...roles], section.roles));

/**
 * Кому открыт хаб `/settings` — объединение ролей всех разделов.
 */
export const SETTINGS_HUB_ROLES: UserRole[] = unionRoles(
  ...SETTINGS_SECTIONS.map((section) => section.roles),
);

export const getSettingsSection = (id: SettingsSectionId): ISettingsSection =>
  SETTINGS_SECTIONS.find((section) => section.id === id) as ISettingsSection;

/**
 * Откроется ли роли раздел (а для «Интеграций» — именно эта вкладка). Для
 * кнопок «Настройки …» на рабочих страницах: ссылка в раздел, которого роль
 * не видит, кончилась бы возвратом в хаб. У «Интеграций» вкладки уже
 * раздела (Kommo — только Admin), поэтому решает роль вкладки.
 */
export const canOpenSettingsSection = (
  roles: readonly UserRole[],
  id: SettingsSectionId,
  integrationTab?: IntegrationTab,
): boolean =>
  visibleFor(
    [...roles],
    id === 'integrations' && integrationTab
      ? INTEGRATION_TAB_ROLES[integrationTab]
      : getSettingsSection(id).roles,
  );

/** Роли раздела — для маршрута в `AppRouter` (`ROLE_LISTED_ROUTES`). */
export const settingsSectionRoles = (id: SettingsSectionId): UserRole[] =>
  getSettingsSection(id).roles;

/** Id команды палитры для раздела: `settingsSms`, `settingsTemplates`… */
export const settingsCommandId = (id: SettingsSectionId): string =>
  `settings${id.charAt(0).toUpperCase()}${id.slice(1)}`;

/* ── Смысловые разделы (ТЗ 0095) ────────────────────────────────────── */

/** Роли, которым виден домен: объединение ролей его карточек. */
export const settingsDomainRoles = (domain: SettingsDomain): UserRole[] =>
  unionRoles(
    ...SETTINGS_SECTIONS.filter((section) =>
      section.domains.includes(domain),
    ).map((section) => section.roles),
  );

/**
 * Домены, в которых роли видна хоть одна карточка, — в порядке
 * `SETTINGS_DOMAINS`. Переключатели роботов (Admin) отдельного условия не
 * требуют: Admin видит все карточки, а значит и все домены.
 */
export const visibleSettingsDomains = (
  roles: readonly UserRole[],
): SettingsDomain[] => {
  const visible = visibleSettingsSections(roles);
  return SETTINGS_DOMAINS.filter((domain) =>
    visible.some((section) => section.domains.includes(domain)),
  );
};

/** Параметры адреса хаба: `?domain=marketing&q=zoom`. */
export const SETTINGS_DOMAIN_PARAM = 'domain';
export const SETTINGS_QUERY_PARAM = 'q';

export interface ISettingsHubFilter {
  domain: SettingsDomain | null;
  query: string;
}

/** Фильтр хаба из адреса; незнакомый домен — «Все». */
export const parseSettingsHubFilter = (search: string): ISettingsHubFilter => {
  const params = new URLSearchParams(search);
  const domain = params.get(SETTINGS_DOMAIN_PARAM);
  return {
    domain: isSettingsDomain(domain) ? domain : null,
    query: params.get(SETTINGS_QUERY_PARAM) ?? '',
  };
};

/** Адрес хаба с фильтром; пустые параметры не пишутся. */
export const settingsHubSearch = ({
  domain,
  query,
}: ISettingsHubFilter): string => {
  const params = new URLSearchParams();
  if (domain) params.set(SETTINGS_DOMAIN_PARAM, domain);
  if (query) params.set(SETTINGS_QUERY_PARAM, query);
  const text = params.toString();
  return text ? `?${text}` : '';
};

/** `/settings?domain=<код>` — для палитры и ссылок. */
export const settingsDomainHref = (domain: SettingsDomain): string =>
  `${PATHS.settings}${settingsHubSearch({ domain, query: '' })}`;

/** Id команды палитры домена: `settingsDomainMarketing`… */
export const settingsDomainCommandId = (domain: SettingsDomain): string =>
  `settingsDomain${domain.charAt(0).toUpperCase()}${domain.slice(1)}`;

const normalize = (text: string): string => text.trim().toLowerCase();

/** Совпадает ли текст с запросом: каждое слово запроса — подстрока. */
export const matchesSettingsQuery = (
  texts: readonly (string | null | undefined)[],
  query: string,
): boolean => {
  const terms = normalize(query).split(/\s+/).filter(Boolean);
  if (!terms.length) return true;
  const haystack = texts
    .filter((text): text is string => Boolean(text))
    .map(normalize)
    .join('\n');
  return terms.every((term) => haystack.includes(term));
};

/**
 * Поиск карточки: название и описание (уже переведённые — их даёт
 * компонент) плюс `keywords` реестра.
 */
export const matchesSettingsSection = (
  section: Pick<ISettingsSection, 'keywords'>,
  title: string,
  desc: string,
  query: string,
): boolean => matchesSettingsQuery([title, desc, ...section.keywords], query);
